/**
 * In-house session replay + error/network capture for pro-web — the
 * self-hosted alternative to LogRocket built out in the Product Analytics
 * project. Additive alongside the existing LogRocket integration
 * (see ./logrocket.ts), not a replacement yet.
 *
 * Talks ONLY to pro-api's /api/analytics/ingest/* routes (never analytics-service
 * directly). Ingestion is optionally-authenticated by design (must keep working
 * pre-login, e.g. a crash on the sign-in page) — userId is a client-declared
 * field read from the decoded access token when present, not derived from an
 * Authorization header.
 *
 * Everything here is best-effort and fire-and-forget: a failure must never be
 * visible to the end user or affect app behavior.
 */
import { record } from "rrweb";
import type { eventWithTime } from "rrweb";
import { decodeAccessTokenPayload } from "../auth/jwt";
import { getAuthCookie } from "../auth/session-cookie";
import { APP_RELEASE } from "./build-info";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3003/api";
const APP_SOURCE = "pro-web";

const ENABLED = import.meta.env.VITE_PRODUCT_ANALYTICS_ENABLED !== "false";

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_MAX_EVENTS = 20;

type ErrorKind = "js_error" | "unhandled_rejection" | "network_error";

interface AnalyticsErrorReport {
  kind: ErrorKind;
  message: string;
  stack?: string;
  httpMethod?: string;
  httpStatus?: number;
  httpUrl?: string;
  severity?: "low" | "medium" | "high" | "critical";
  meta?: Record<string, unknown>;
}

let sessionId: string | null = null;
let sessionStartedAtMs: number | null = null;
let sessionCreatePromise: Promise<string | null> | null = null;
let stopRecording: (() => void) | null = null;
let eventBuffer: eventWithTime[] = [];
let chunkSeq = 0;
let flushTimer: ReturnType<typeof setInterval> | null = null;

// The access token lives in a shared cookie (set by atoms.ts, readable across
// the ecosystem's apps), not localStorage — sessions were silently recording
// as "Anonymous" for every logged-in user because this read the wrong store
// and always got null.
function currentTokenPayload() {
  try {
    return decodeAccessTokenPayload(getAuthCookie("access_token"));
  } catch {
    return null;
  }
}

function currentUserId(): string | undefined {
  return currentTokenPayload()?.sub;
}

function currentOrganizationId(): string | undefined {
  return currentTokenPayload()?.org_id;
}

async function postJson(path: string, body: unknown, options: { keepalive?: boolean } = {}): Promise<Response> {
  return fetch(`${API_BASE_URL}${path}`, {
    method: path.endsWith("/close") ? "PATCH" : "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: options.keepalive,
  });
}

async function createSession(): Promise<string | null> {
  try {
    const res = await postJson("/analytics/ingest/sessions", {
      appSource: APP_SOURCE,
      userId: currentUserId(),
      organizationId: currentOrganizationId(),
      urlPath: window.location.pathname,
      userAgent: navigator.userAgent,
      appVersion: APP_RELEASE,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { sessionId?: string };
    return data.sessionId ?? null;
  } catch {
    // Analytics must never break the app — silently give up on this session.
    return null;
  }
}

async function flushEventBuffer(useBeacon = false): Promise<void> {
  if (!sessionId || eventBuffer.length === 0) return;
  const batch = eventBuffer;
  eventBuffer = [];
  const seq = chunkSeq++;

  // NOTE: sent as plain base64(JSON), not gzip'd yet, despite the ingest
  // schema's doc comment describing gzip — a real compression pass (e.g. via
  // CompressionStream) is a worthwhile follow-up, not required for this to
  // work correctly (analytics-service just stores whatever bytes it's given).
  const data = btoa(unescape(encodeURIComponent(JSON.stringify(batch))));
  const path = `/analytics/ingest/sessions/${sessionId}/chunks`;

  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify({ seq, data })], { type: "application/json" });
      navigator.sendBeacon(`${API_BASE_URL}${path}`, blob);
      return;
    }
    await postJson(path, { seq, data });
  } catch {
    // Drop the batch — never retry-loop or surface this to the user.
  }
}

function closeSession(): void {
  if (!sessionId) return;
  void flushEventBuffer(true);
  try {
    void postJson(`/analytics/ingest/sessions/${sessionId}/close`, {
      endedAt: new Date().toISOString(),
      durationMs: sessionStartedAtMs != null ? Date.now() - sessionStartedAtMs : undefined,
    }, { keepalive: true });
  } catch {
    // best-effort
  }
}

function startRecording(): void {
  if (stopRecording) return;
  stopRecording = record({
    emit(event) {
      eventBuffer.push(event);
      if (eventBuffer.length >= FLUSH_MAX_EVENTS) void flushEventBuffer();
    },
    // PII masking — baked in at capture time, not replay time.
    maskAllInputs: true,
    maskTextClass: "analytics-mask",
    blockClass: "analytics-block",
    checkoutEveryNms: 30_000,
  }) ?? null;

  flushTimer = setInterval(() => void flushEventBuffer(), FLUSH_INTERVAL_MS);

  window.addEventListener("pagehide", () => {
    if (flushTimer) clearInterval(flushTimer);
    closeSession();
  });
}

/** Exported so ErrorPage (and anywhere else) can dual-write alongside the existing browser-errors reporter. */
export function reportAnalyticsError(report: AnalyticsErrorReport): void {
  if (!ENABLED) return;
  void postJson("/analytics/ingest/errors", {
    events: [
      {
        appSource: APP_SOURCE,
        userId: currentUserId(),
        organizationId: currentOrganizationId(),
        sessionId: sessionId ?? undefined,
        kind: report.kind,
        message: report.message.slice(0, 4096),
        stack: report.stack?.slice(0, 16384),
        urlPath: window.location.pathname,
        httpMethod: report.httpMethod,
        httpStatus: report.httpStatus,
        httpUrl: report.httpUrl,
        severity: report.severity ?? "medium",
        appVersion: APP_RELEASE,
        meta: report.meta,
        occurredAt: new Date().toISOString(),
      },
    ],
  }).catch(() => {
    // Never let telemetry reporting interfere with the app.
  });
}

function installGlobalErrorHandlers(): void {
  window.addEventListener("error", (event) => {
    reportAnalyticsError({
      kind: "js_error",
      message: event.message || "Unknown error",
      stack: event.error?.stack,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as unknown;
    const message = reason instanceof Error ? reason.message : String(reason ?? "Unhandled rejection");
    const stack = reason instanceof Error ? reason.stack : undefined;
    reportAnalyticsError({ kind: "unhandled_rejection", message, stack });
  });
}

function installFetchNetworkCapture(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const [input, init] = args;
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");

    // Never report failures from analytics' own ingestion calls — avoids a
    // self-referential loop and noise from analytics-service itself being down.
    const isOwnIngestCall = url.includes("/analytics/ingest/");

    try {
      const response = await originalFetch(...args);
      if (!response.ok && !isOwnIngestCall && response.status >= 400) {
        reportAnalyticsError({
          kind: "network_error",
          message: `${method} ${url} → ${response.status}`,
          httpMethod: method,
          httpStatus: response.status,
          httpUrl: url,
          severity: response.status >= 500 ? "high" : "low",
        });
      }
      return response;
    } catch (err) {
      if (!isOwnIngestCall) {
        reportAnalyticsError({
          kind: "network_error",
          message: err instanceof Error ? err.message : `Network request failed: ${method} ${url}`,
          httpMethod: method,
          httpUrl: url,
          severity: "medium",
        });
      }
      throw err;
    }
  };
}

/** Call once at app boot, alongside initLogRocket(). No-ops if disabled via VITE_PRODUCT_ANALYTICS_ENABLED=false. */
export function initProductAnalytics(): void {
  if (!ENABLED) return;
  if (sessionCreatePromise) return;

  installGlobalErrorHandlers();
  installFetchNetworkCapture();

  sessionCreatePromise = createSession().then((id) => {
    sessionId = id;
    if (id) {
      sessionStartedAtMs = Date.now();
      deferStartRecording();
    }
    return id;
  });
}

/**
 * rrweb's initial snapshot walks the live DOM and every stylesheet's CSSOM
 * synchronously to build a faithful first frame — on a heavy dev-mode SPA
 * (Vanilla Extract injects one <style> tag per CSS module, so there can be
 * hundreds) this was measured to block the main thread long enough that the
 * app's own first render never got a chance to happen at all. Deferring the
 * recorder's start until the browser is idle (well after initial mount) lets
 * the app become interactive first; recording then starts a beat later
 * instead of contending with first paint.
 */
function deferStartRecording(): void {
  const idle = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;
  if (idle) {
    idle(() => startRecording(), { timeout: 5000 });
  } else {
    setTimeout(startRecording, 2000);
  }
}
